import React, { useState, useEffect } from 'react';
import api from '../../services/api';

/**
 * Onglet admin « Audience » — mesure first-party sans cookie (table page_events).
 * Lit GET /admin/audience : fenêtres aujourd'hui/7j/30j, trafic quotidien,
 * pays, appareils, navigateurs, langues, pages, provenances, CTA.
 *
 * Accessibilité daltonisme : les deux séries du graphe (vues / visiteurs) sont
 * distinguées par la LUMINOSITÉ (clair vs foncé), jamais par la teinte seule.
 */

// Drapeau emoji depuis un code ISO alpha-2 ('BE' → 🇧🇪).
const flag = (cc) => cc.replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));

const COUNTRY_NAMES = {
    BE: 'Belgique', FR: 'France', NL: 'Pays-Bas', LU: 'Luxembourg', DE: 'Allemagne',
    CH: 'Suisse', GB: 'Royaume-Uni', US: 'États-Unis', CA: 'Canada', ES: 'Espagne',
    IT: 'Italie', PT: 'Portugal', MA: 'Maroc', TN: 'Tunisie', DZ: 'Algérie',
};
const countryLabel = (cc) => `${flag(cc)} ${COUNTRY_NAMES[cc] || cc}`;

const FR_DAY = (iso) => {
    const d = new Date(`${iso}T00:00:00Z`);
    return d.toLocaleDateString('fr-BE', { day: 'numeric', month: 'short', timeZone: 'UTC' });
};

/** Complète les 30 derniers jours (UTC) avec des zéros pour les jours sans trafic. */
const fillDays = (rows) => {
    const byDay = Object.fromEntries((rows || []).map((r) => [r.day, r]));
    const days = [];
    const today = new Date();
    for (let i = 29; i >= 0; i--) {
        const d = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - i));
        const key = d.toISOString().slice(0, 10);
        days.push({ day: key, views: byDay[key]?.views || 0, uniques: byDay[key]?.uniques || 0 });
    }
    return days;
};

/** Carte KPI double valeur (visiteurs en grand, vues en dessous). */
const WindowCard = ({ title, icon, stats, color }) => (
    <div className="relative overflow-hidden rounded-xl bg-white p-6 shadow-lg">
        <div className={`absolute right-0 top-0 -mt-4 -mr-4 h-24 w-24 rounded-full bg-gradient-to-br ${color} opacity-20 blur-2xl`} />
        <div className="relative z-10">
            <p className="text-sm font-medium text-gray-500">{icon} {title}</p>
            <h3 className="mt-2 text-3xl font-bold text-gray-800">
                {stats.visitors}
                <span className="ml-1.5 text-sm font-medium text-gray-400">visiteur{stats.visitors > 1 ? 's' : ''}</span>
            </h3>
            <p className="mt-1 text-sm text-gray-500">{stats.views} page{stats.views > 1 ? 's' : ''} vue{stats.views > 1 ? 's' : ''}</p>
        </div>
    </div>
);

/** Petit tableau de répartition avec barres proportionnelles (visiteurs uniques). */
const Breakdown = ({ title, icon, rows, emptyLabel }) => {
    const max = Math.max(...rows.map(([, n]) => n), 1);
    return (
        <div className="rounded-xl bg-white p-5 shadow-lg">
            <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-gray-500">{icon} {title}</h3>
            {rows.length === 0 ? (
                <p className="py-4 text-center text-sm text-gray-400">{emptyLabel || 'Pas encore de données'}</p>
            ) : (
                <ul className="space-y-2">
                    {rows.map(([label, n], i) => (
                        <li key={i} className="text-sm">
                            <div className="flex items-baseline justify-between gap-2">
                                <span className="truncate font-medium text-gray-700">{label}</span>
                                <span className="tabular-nums text-gray-500">{n}</span>
                            </div>
                            <div className="mt-1 h-1.5 rounded-full bg-gray-100">
                                <div
                                    className="h-1.5 rounded-full bg-gradient-to-r from-teal-400 to-teal-600"
                                    style={{ width: `${(n / max) * 100}%` }}
                                />
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

const SectionTable = ({ title, icon, columns, rows, emptyLabel }) => (
    <div className="rounded-xl bg-white p-6 shadow-lg">
        <h3 className="mb-4 text-lg font-bold text-gray-800">{icon} {title}</h3>
        {rows.length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-400">{emptyLabel}</p>
        ) : (
            <table className="w-full text-sm">
                <thead>
                    <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-400">
                        {columns.map((c) => (
                            <th key={c} className="pb-2 font-medium last:text-right">{c}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((cells, i) => (
                        <tr key={i} className="border-b border-gray-50 last:border-0">
                            {cells.map((cell, j) => (
                                <td key={j} className={`py-2 ${j === 0 ? 'font-medium text-gray-700' : 'text-gray-500'} last:text-right last:tabular-nums`}>
                                    {cell}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        )}
    </div>
);

const AudienceDashboard = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        api.get('/admin/audience')
            .then((res) => setData(res.data))
            .catch((err) => {
                console.error('Error fetching audience stats:', err);
                setError("Impossible de charger les données d'audience.");
            })
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <div className="dashboard-loading">Chargement de l'audience...</div>;
    if (error) return <div className="dashboard-error">{error}</div>;
    if (!data) return null;

    const { audience, totals, trafficByDay, pages, ctas, referrers, countries, devices, browsers, langs } = data;
    const days = fillDays(trafficByDay);
    const maxViews = Math.max(...days.map((d) => d.views), 1);
    const hasTraffic = days.some((d) => d.views > 0);

    const deviceLabel = (d) => (d === 'mobile' ? '📱 Mobile' : '🖥️ Ordinateur');

    return (
        <div className="space-y-6">
            {/* KPI : trois fenêtres + CTA */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
                <WindowCard title="Aujourd'hui" icon="📅" stats={audience.today} color="from-purple-500 to-purple-600" />
                {/* « visiteurs-jour » et non « visiteurs » : l'empreinte est
                    salee par jour, donc quelqu'un qui revient trois jours compte
                    trois fois sur ces deux fenetres. Seule la carte
                    « Aujourd'hui » compte de vraies personnes. */}
                <WindowCard title="7 derniers jours (visiteurs-jour)" icon="📈" stats={audience.week} color="from-blue-500 to-blue-600" />
                <WindowCard title="30 derniers jours (visiteurs-jour)" icon="🗓️" stats={audience.month} color="from-emerald-500 to-emerald-600" />
                <div className="relative overflow-hidden rounded-xl bg-white p-6 shadow-lg">
                    <div className="absolute right-0 top-0 -mt-4 -mr-4 h-24 w-24 rounded-full bg-gradient-to-br from-orange-500 to-orange-600 opacity-20 blur-2xl" />
                    <div className="relative z-10">
                        <p className="text-sm font-medium text-gray-500">🎯 Clics CTA (30 j)</p>
                        <h3 className="mt-2 text-3xl font-bold text-gray-800">{totals.cta30}</h3>
                        <p className="mt-1 text-sm text-gray-500">boutons d'action cliqués</p>
                    </div>
                </div>
            </div>

            {/* Trafic quotidien */}
            <div className="rounded-xl bg-white p-6 shadow-lg">
                <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                    <h3 className="text-lg font-bold text-gray-800">Trafic quotidien (30 jours)</h3>
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span className="flex items-center gap-1.5">
                            <span className="inline-block h-3 w-3 rounded-sm bg-teal-300" /> Vues
                        </span>
                        <span className="flex items-center gap-1.5">
                            <span className="inline-block h-3 w-3 rounded-sm bg-slate-800" /> Visiteurs uniques
                        </span>
                    </div>
                </div>
                {!hasTraffic && (
                    <p className="mb-2 text-xs text-gray-400">
                        En attente des premières visites — les compteurs tournent depuis le 15/07/2026.
                    </p>
                )}
                <div className="flex h-56 items-end gap-[3px]">
                    {days.map((d) => {
                        const vH = (d.views / maxViews) * 100;
                        const uH = (d.uniques / maxViews) * 100;
                        return (
                            <div
                                key={d.day}
                                className="group relative flex h-full flex-1 items-end justify-center"
                                title={`${FR_DAY(d.day)} : ${d.views} vues, ${d.uniques} visiteurs uniques`}
                            >
                                {/* Vues = barre claire ; uniques = barre foncée imbriquée (contraste de luminosité) */}
                                <div
                                    className="w-full rounded-t bg-teal-300 transition-all duration-500"
                                    style={{ height: `${Math.max(vH, d.views > 0 ? 3 : 1)}%` }}
                                />
                                <div
                                    className="absolute bottom-0 w-1/2 rounded-t bg-slate-800"
                                    style={{ height: `${Math.max(uH, d.uniques > 0 ? 3 : 0)}%` }}
                                />
                            </div>
                        );
                    })}
                </div>
                <div className="mt-2 flex justify-between text-[10px] text-gray-400">
                    <span>{FR_DAY(days[0].day)}</span>
                    <span>{FR_DAY(days[14].day)}</span>
                    <span>{FR_DAY(days[29].day)}</span>
                </div>
            </div>

            {/* Répartitions : pays, appareils, navigateurs, langues (visiteurs uniques, 30 j) */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                <Breakdown
                    title="Pays (30 j)"
                    icon="🌍"
                    rows={(countries || []).map((c) => [countryLabel(c.country), c.visitors])}
                    emptyLabel="Pays inconnu pour l'instant"
                />
                <Breakdown
                    title="Appareils (30 j)"
                    icon="📱"
                    rows={(devices || []).map((d) => [deviceLabel(d.device), d.visitors])}
                />
                <Breakdown
                    title="Navigateurs (30 j)"
                    icon="🧭"
                    rows={(browsers || []).map((b) => [b.browser, b.visitors])}
                />
                <Breakdown
                    title="Langues (30 j)"
                    icon="🗣️"
                    rows={(langs || []).map((l) => [String(l.lang || '—').toUpperCase(), l.visitors])}
                />
            </div>

            {/* Détails */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <SectionTable
                    title="Pages les plus vues (30 j)"
                    icon="📄"
                    columns={['Page', 'Visiteurs', 'Vues']}
                    rows={(pages || []).map((p) => [p.page, p.uniques, p.views])}
                    emptyLabel="Aucune vue enregistrée pour l'instant"
                />
                <SectionTable
                    title="Provenance des visites (30 j)"
                    icon="🔗"
                    columns={["Site d'origine", 'Visiteurs']}
                    rows={(referrers || []).map((r) => [r.referrer, r.visitors])}
                    emptyLabel="Aucune provenance externe pour l'instant (accès directs)"
                />
            </div>

            <SectionTable
                title="Clics sur les boutons d'action (30 j)"
                icon="🎯"
                columns={['Bouton (page:emplacement)', 'Clics']}
                rows={(ctas || []).map((c) => [c.page, c.clicks])}
                emptyLabel="Aucun clic CTA enregistré pour l'instant"
            />

            <p className="text-xs text-gray-400">
                Mesure first-party anonyme : aucun cookie, aucune adresse IP stockée, bots exclus.
                Le pays provient de l'en-tête Cloudflare, l'appareil et le navigateur sont déduits
                sans conserver le user-agent. Les visiteurs uniques sont comptés par jour (hash
                journalier non recoupable d'un jour à l'autre).
            </p>
        </div>
    );
};

export default AudienceDashboard;
